import { useLang } from "../context/LanguageContext";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { MapPin, Clock, Wallet, Lock, Facebook, Linkedin, Instagram, Globe, Mail, Phone, User, CheckCircle2 } from "lucide-react";

const sourceIcon = { facebook: Facebook, linkedin: Linkedin, instagram: Instagram, web: Globe };

function timeAgo(iso, lang) {
  const diff = (Date.now() - new Date(iso).getTime()) / 36e5;
  if (diff < 1) return lang === "fr" ? "moins d'1h" : "<1h";
  if (diff < 24) return `${Math.floor(diff)}h`;
  return `${Math.floor(diff / 24)}${lang === "fr" ? "j" : "d"}`;
}

export const RequestCard = ({ req, sectorLabel, onUnlock, subscribed }) => {
  const { t, lang } = useLang();
  const SrcIcon = sourceIcon[req.source] || Globe;

  return (
    <div
      data-testid={`request-card-${req.id}`}
      className="group relative flex flex-col rounded-2xl border border-border bg-card p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <Badge variant="secondary" className="rounded-full font-medium">
          {sectorLabel}
        </Badge>
        <div className="flex items-center gap-2">
          {req.urgency === "urgent" && (
            <Badge className="rounded-full bg-destructive text-destructive-foreground">
              {t("urgent_badge")}
            </Badge>
          )}
          <span className="grid place-items-center h-7 w-7 rounded-full bg-secondary text-muted-foreground" title={req.source}>
            <SrcIcon className="h-3.5 w-3.5" />
          </span>
        </div>
      </div>

      <h3 className="font-display text-lg font-semibold leading-snug mb-2">{req.title}</h3>
      <p className="text-sm text-muted-foreground leading-relaxed mb-4 line-clamp-3">{req.description}</p>

      <div className="mt-auto space-y-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{req.location}</span>
          {req.budget && <span className="inline-flex items-center gap-1.5"><Wallet className="h-3.5 w-3.5" />{req.budget}</span>}
          <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{t("posted_ago")} {timeAgo(req.created_at, lang)}</span>
        </div>

        <div className="border-t border-border pt-3">
          {req.unlocked ? (
            <div className="space-y-1.5" data-testid={`contact-${req.id}`}>
              <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-600">
                <CheckCircle2 className="h-3.5 w-3.5" /> {t("unlocked")}
              </div>
              <p className="text-sm inline-flex items-center gap-2"><User className="h-3.5 w-3.5 text-muted-foreground" />{req.contact_name}</p>
              <p className="text-sm inline-flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-muted-foreground" />{req.contact_email}</p>
              {req.contact_phone && <p className="text-sm inline-flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-muted-foreground" />{req.contact_phone}</p>}
            </div>
          ) : (
            <Button
              variant="default"
              size="sm"
              data-testid={`unlock-btn-${req.id}`}
              onClick={() => onUnlock(req)}
              className="w-full"
            >
              <Lock className="h-3.5 w-3.5 mr-1.5" />
              {t("unlock")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
